/* eslint-disable unicorn/no-object-as-default-parameter */
import { ApolloCache, isReference } from '@apollo/client'
import {
  CanReadFunction,
  Modifier,
  ReadFieldFunction,
  ToReferenceFunction,
} from '@apollo/client/cache/core/types/common'
import { StorageType } from '@apollo/client/cache/inmemory/policies'
import { parse } from 'cookie'

import { TasksProgressDocument } from '~generated/hooks'
import { TasksProgressQuery, TasksProgressFragment } from '~generated/operations'
import { TaskStatusTypes } from '~generated/types'
import { isBrowser } from '~lib/helpers'
import Logger from '~shared/logger'

type MergePaginatedObjByFieldOptions = {
  field?: string
}

export const mergePaginatedObj = (options?: MergePaginatedObjByFieldOptions) => {
  return (existing, incoming, { readField }) => {
    const { field } = options || {}
    if (existing && incoming.page !== 1) {
      const existingDocs = [...(existing.docs || [])]

      let incomingDocs = incoming.docs

      if (field) {
        const existingDocsIds = new Set(existingDocs.map((ref) => readField(field, ref)))
        incomingDocs = incoming.docs.filter((ref) => {
          return !existingDocsIds.has(readField(field, ref))
        })
      }

      const docs = [...existingDocs, ...incomingDocs]

      return {
        ...existing,
        ...incoming,
        docs,
      }
    }

    return incoming
  }
}

export const mergeDocs = (existing = [], incoming = []) => {
  return [...existing, ...incoming]
}

export const getCacheCookie = (cookie?: Record<string, string>) => {
  return isBrowser() ? parse(document.cookie) : cookie
}

/**
 * Update launch tasks progress helper
 * Updates progress on task status change and on create / duplicate task
 */
interface UpdateProgressQueryInput {
  launchId: string
  status?: TaskStatusTypes
  prevStatus?: TaskStatusTypes
}

const taskStatusProgressRelation: Partial<Record<TaskStatusTypes, keyof Omit<TasksProgressFragment, '__typename'>>> = {
  [TaskStatusTypes.NOT_STARTED]: 'notStartedCount',
  [TaskStatusTypes.IN_PROCESS]: 'inProgressCount',
  [TaskStatusTypes.IN_REVIEW]: 'inReviewCount',
  [TaskStatusTypes.DONE]: 'completedCount',
  [TaskStatusTypes.CANCELED]: 'canceledCount',
  [TaskStatusTypes.DELAYED]: 'delayedCount',
}

export const updateTasksProgressQuery = (cache: ApolloCache<any>, input: UpdateProgressQueryInput) => {
  const { launchId, status, prevStatus } = input

  cache.updateQuery<TasksProgressQuery>(
    {
      query: TasksProgressDocument,
      variables: { input: { launchId } },
    },
    (progressQuery) => {
      const progress = { ...progressQuery, tasksProgress: { ...progressQuery.tasksProgress } }

      if (status) {
        progress.tasksProgress[taskStatusProgressRelation[status]] += 1
      }

      if (prevStatus) {
        progress.tasksProgress[taskStatusProgressRelation[prevStatus]] -= 1
      }

      return progress
    }
  )
}

export function sortReferences<T extends any = any>(
  refs: T[],
  comparer: (a: T, b: T, readField: ReadFieldFunction) => number,
  readField: ReadFieldFunction
) {
  return [...refs].sort((a, b) => comparer(a, b, readField))
}

// to handle parameterized queries
export type ApolloModifierDetails = {
  DELETE: any
  INVALIDATE: any
  fieldName: string
  storeFieldName: string
  readField: ReadFieldFunction
  canRead: CanReadFunction
  isReference: typeof isReference
  toReference: ToReferenceFunction
  storage: StorageType
}
export function wrapQueryModifier<Value, Variables extends Record<string, any>>(
  cb: (value: Value, details: ApolloModifierDetails & { variables?: Variables }) => Value
): Modifier<Value> {
  return (value, details) => {
    const { storeFieldName } = details

    const jsonVariables = storeFieldName.slice(storeFieldName.indexOf('{'), storeFieldName.lastIndexOf('}') + 1)

    let variables: Variables | undefined

    if (jsonVariables) {
      try {
        variables = JSON.parse(jsonVariables)
      } catch (error) {
        Logger.getInstance().trackError(error)
      }
    }

    return cb(value, { ...details, variables })
  }
}
